import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import FlagIcon from "@mui/icons-material/Flag";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { parse, format, isValid } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MuiAlert from "@mui/material/Alert";
import { hasPermission } from "../utils/auth";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import {
  Dialog,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Alert,
  Tooltip,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Paper,
  Box,
  Typography,
  ThemeProvider,
  CssBaseline,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import Table_theme from "../Themes/TableTheme";
import { formatDateLocal, formatDateLocal_TF } from "../utils/dateUtils";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const projectFields = [
  "proj_name",
  "description",
  "start_date",
  "planned_end_date",
  "actual_enddate",
  "projectstatus",
  "progresspercentage",
  "risklevel",
  "budgetamount",
  "currencycode",
  "billingtype",
  "invoicingcycle",
  "approval_level_required",
  "technicalleadid",
  "projectmanagerid",
  "hrid",
  "clientpoc",
  "clientpocemail",
  "deliverymodel",
  "location",
  "timezone",
];

const formFields = projectFields.filter(
  (f) => f !== "cl_proj_id" && f !== "proj_id"
);
const fieldLabels = {
  cl_proj_id: "Client Project ID",
  cl_id: "Client ID",
  proj_id: "Project ID",
  proj_name: "Project Name",
  description: "Description",
  start_date: "Start Date",
  planned_end_date: "Planned End Date",
  actual_enddate: "Actual End Date",
  projectstatus: "Status",
  progresspercentage: "Percentage",
  risklevel: "Risk Level",
  budgetamount: "Budget",
  currencycode: "Currency",
  billingtype: "Billing Type",
  invoicingcycle: "Invoicing Cycle",
  projectmanagerid: "Project Manager",
  technicalleadid: "Project Lead",

  hrid: "HR Manager",
  approval_level_required: "Approval Level",
  clientpoc: "Client POC",
  clientpocemail: "Client POC email",
  deliverymodel: "Delivery Model",
  location: "Location",
  timezone: "Timezone",
};
const initialFormState = {
  proj_id: "",
  cl_name: "",
  proj_name: "",
  description: "",
  start_date: "",
  planned_end_date: "",
  actual_enddate: "",
  projectstatus: "",
  progresspercentage: "",
  risklevel: "",
  budgetamount: "",
  currencycode: "",
  billingtype: "",
  invoicingcycle: "",
  projectmanagerid: "",
  technicalleadid: "",
  hrid: "",
  clientpoc: "",
  clientpocemail: "",
  deliverymodel: "",
  location: "",
  timezone: "",
};
const clientProjectTheme = createTheme({
  typography: {
    fontFamily: "Public Sans, sans-serif",
    fontSize: 13,
  },
});
const ClientProjectSubpage = ({ open, onClose, cl_id, bo_id }) => {
  const { loading, userId, empId, boId, roles, hasPermission } =
    useCurrentUser();
  const [clientProjects, setClientProjects] = useState([]);
  const [newProject, setNewProject] = useState({
    cl_id,
    bo_id,
  });
  const [alertMsg, setAlertMsg] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [filters, setFilters] = useState({ deleteFlag: "All" });
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formState, setFormState] = useState({ initialFormState });
  const [approvalLevel, setApprovalLevel] = useState([]);
  const [projectStatus, setProjectStatus] = useState([]);
  const [riskLevel, setRiskLevel] = useState([]);
  const [currencyCodes, setCurrencyCodes] = useState([]);
  const [invoiceCycle, setInvoiceCycle] = useState([]);
  const [deliveryModel, setDeliveryModel] = useState([]);
  const [riskLevels, setRiskLevels] = useState([]);
  const [billingTypes, setBillingTypes] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [countries, setCountries] = useState([]);
  const [employeeNameMap, setEmployeeNameMap] = useState({});
  const [editProject, setEditProject] = useState({});
  const [activeForm, setActiveForm] = useState(null);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errors, setErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [hrd, setHRs] = useState([]);
  const [clientName, setClientName] = useState("");
  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);
  const [tls, setTLs] = useState([]);
  const toDateSafe = (value) => {
    if (!value) return null;
    // Already a Date
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value;
    }
    // yyyy-MM-dd format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parsed = parse(value, "yyyy-MM-dd", new Date());
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    // Try parsing other string formats (e.g., ISO or locale-specific)
    const parsedDate = new Date(value);
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const fetchClientProjects = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/clientprojects/paginated`,
        {
          params: { cl_id, page: 1, limit: 10, boId, empId, roles, filters },
        }
      );
      setClientProjects(response.data.data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  }, [cl_id]);

  useEffect(() => {
    if (open && cl_id) fetchClientProjects();
  }, [open, cl_id, fetchClientProjects]);

  const fetchClientName = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/clients/${boId}/${cl_id}`
      );
      setClientName(response.data.cl_name || "");
    } catch (err) {
      console.error("Client name fetch error:", err);
    }
  }, [cl_id, boId]); // include boId in dependencies

  useEffect(() => {
    if (open && cl_id) {
      fetchClientProjects();
      fetchClientName();
    }
  }, [open, cl_id, fetchClientProjects, fetchClientName]);

  const commonSx = {
    "& .MuiInputBase-root": {
      fontSize: "14px",
      minHeight: 30,
      borderRadius: "9px",
      backgroundColor: "#fafafa",
      color: "#292727ff",
    },
    "& .MuiInputBase-input": {
      fontSize: "14px",
      padding: "8.5px 14px",
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
     
    },
    "& .MuiSelect-select": {
      fontSize: "14px",
      padding: "8.5px 14px",
    },
    mt: 0.5,
    mb: 0,
    width: "200px",
  };
  const renderField = (field, state, setState, fieldErrors) => {
    const isDate = [
      "start_date",
      "planned_end_date",
      "actual_enddate",
    ].includes(field);
    const sharedProps = {
      fullWidth: true,
      size: "small",
      InputLabelProps: {
        shrink: true,
        sx: { fontSize: "0.85rem", fontWeight: 500, mt: 0.5 },
      },
      inputProps: {
        style: { fontSize: "0.85rem", fontFamily: "Public Sans, sans-serif" },
      },
    };
    // Common change handler
    const handleChange = (e) => {
      const { name, value } = e.target;
      setState((prev) => ({
        ...prev,
        [name]:
          name === "approval_level_required" || name === "progresspercentage"
            ? Number(value)
            : value,
      }));
      // Inline validation
      setErrors((prev) => {
        const newErrors = { ...prev };

        if (name === "progresspercentage") {
          const numValue = Number(value);
          if (isNaN(numValue)) {
            newErrors[name] = "Progress must be a number";
          } else if (numValue < 0 || numValue > 100) {
            newErrors[name] = "Progress must be between 0 and 100";
          } else {
            newErrors[name] = ""; // clear error if valid
          }
        } else {
          // clear other field errors
          newErrors[name] = "";
        }

        return newErrors;
      });
    };
    if (field === "progresspercentage") {
      return (
        <TextField
          label="Progress %"
          name="progresspercentage"
          type="number"
          value={state.progresspercentage || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.progresspercentage)}
          helperText={errors.progresspercentage || ""}
          sx={commonSx}
          inputProps={{ min: 0, max: 100 }} // restrict user input
        />
      );
    }

    // Manager dropdown
    if (field === "projectmanagerid") {
      const approvalLevel = Number(state.approval_level_required);
      const isDisabled = approvalLevel < 2;
      return (
        <TextField
          select
          label="Project Manager *"
          name="projectmanagerid"
          value={state.projectmanagerid || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
          disabled={isDisabled}
          error={Boolean(errors.hrid)}
        
          SelectProps={{
            renderValue: (val) => {
              const found = managers.find((m) => m.employee_id === val);
              return found ? found.employee_name : val;
            },
          }}
        >
          {managers.map((mgr) => (
            <MenuItem key={mgr.employee_id} value={mgr.employee_id} sx={{ fontSize: "12px" }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>{mgr.employee_name}</Typography>
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>{mgr.employee_id}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // TL dropdown
    if (field === "technicalleadid") {
      const approvalLevel = Number(state.approval_level_required);
      const isDisabled = approvalLevel < 1;
      return (
        <TextField
          select
          label="Technical Lead *"
          name="technicalleadid"
          value={state.technicalleadid || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
          disabled={isDisabled}
          error={Boolean(errors.hrid)}
          helperText={isDisabled ? "Not required for this approval level" : errors.technicalleadid || ""}
          SelectProps={{
            renderValue: (val) => {
              const found = tls.find((t) => t.employee_id === val);
              return found ? found.employee_name : val;
            },
          }}
        >
          {tls.map((tl) => (
            <MenuItem key={tl.employee_id} value={tl.employee_id} sx={{ fontSize: "12px" }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>{tl.employee_name}</Typography>
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>{tl.employee_id}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // HR dropdown
    if (field === "hrid") {
      const approvalLevel = Number(state.approval_level_required);
      const isDisabled = approvalLevel !== 3;
      return (
        <TextField
          select
          label="HR Manager *"
          name="hrid"
          value={state.hrid || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
          disabled={isDisabled}
          error={Boolean(errors.hrid)}
         
          SelectProps={{
            renderValue: (val) => {
              const found = hrd.find((h) => h.employee_id === val);
              return found ? found.employee_name : val;
            },
          }}
        >
          {hrd.map((tl) => (
            <MenuItem key={tl.employee_id} value={tl.employee_id} sx={{ fontSize: "12px" }}>
              <Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>{tl.employee_name}</Typography>
                <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>{tl.employee_id}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      );
    }
    if (field === "approval_level_required") {
      return (
        <TextField
          select
          required
          label="Approval Level Required "
          name="approval_level_required"
          value={state.approval_level_required ?? ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
          error={Boolean(errors.approval_level_required)}
          helperText={errors.approval_level_required || ""}
        >
          {approvalLevel.map((al) => (
            <MenuItem
              key={al.variable_code}
              value={al.variable_code.replace("LEVEL", "")} // converts LEVEL1 → "1"
              sx={{ fontSize: "12px" }}
            >
              {al.variable_value} {/* shows "Level 1", "Level 2", etc. */}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    // Location dropdown
    if (field === "location") {
      return (
        <TextField
          select
          label="Location"
          name="location"
          value={state.location || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {countries.map((loc) => (
            <MenuItem
              key={loc.country_id}
              value={loc.country_name}
              sx={{ fontSize: "12px" }}
            >
              {loc.country_name}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // Timezone dropdown
    if (field === "timezone") {
      const tzList = Array.isArray(timezones) ? timezones : [];
      return (
        <TextField
          select
          label="Timezone"
          name="timezone"
          value={state.timezone || ""}
          onChange={handleChange}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {tzList.map((tz) => (
            <MenuItem
              key={tz.variable_code}
              value={tz.variable_code}
              sx={{ fontSize: "12px" }}
            >
              {tz.variable_code}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // project status dropdown
    if (field === "projectstatus") {
      return (
        <TextField
          select
          label="Project Status"
          name="projectstatus"
          value={state.projectstatus || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {projectStatus.map((ps) => (
            <MenuItem
              key={ps.variable_code}
              value={ps.variable_value}
              sx={{ fontSize: "12px" }}
            >
              {ps.variable_value}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // currency code dropdown
    if (field === "currencycode") {
      return (
        <TextField
          select
          label="Currency Code"
          name="currencycode"
          value={state.currencycode || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {currencyCodes.map((cc) => (
            <MenuItem
              key={cc.variable_code}
              value={cc.variable_code}
              sx={{ fontSize: "12px" }}
            >
              {cc.variable_code}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // Risk level dropdown
    if (field === "risklevel") {
      return (
        <TextField
          select
          label="Risk Level"
          name="risklevel"
          value={state.risklevel || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {riskLevel.map((rl) => (
            <MenuItem
              key={rl.variable_code}
              value={rl.variable_value}
              sx={{ fontSize: "12px" }}
            >
              {rl.variable_value}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    // billing type dropdown
    if (field === "billingtype") {
      return (
        <TextField
          select
          label="Billing Type"
          name="billingtype"
          value={state.billingtype || ""}
          onChange={handleChange}
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={commonSx}
        >
          {billingTypes.map((bt) => (
            <MenuItem
              key={bt.variable_code}
              value={bt.variable_value}
              sx={{ fontSize: "12px" }}
            >
              {bt.variable_value}
            </MenuItem>
          ))}
        </TextField>
      );
    }
       // Invoicing Cycle type dropdown
        if (field === "invoicingcycle") {
          return (
            <TextField
              select
              label="Invoicing Cycle"
              name="invoicingcycle"
              value={state.invoicingcycle || ""}
              onChange={handleChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={commonSx}
            >
              {invoiceCycle.map((bt) => (
                <MenuItem
                  key={bt.variable_code}
                  value={bt.variable_value}
                  sx={{ fontSize: "12px" }}
                >
                  {bt.variable_value}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        // Deliver model Cycle type dropdown
        if (field === "deliverymodel") {
          return (
            <TextField
              select
              label="Delivery Model"
              name="deliverymodel"
              value={state.deliverymodel || ""}
              onChange={handleChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={commonSx}
            >
              {deliveryModel.map((bt) => (
                <MenuItem
                  key={bt.variable_code}
                  value={bt.variable_value}
                  sx={{ fontSize: "12px" }}
                >
                  {bt.variable_value}
                </MenuItem>
              ))}
            </TextField>
          );
        }
    // Date fields
    return isDate ? (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={fieldLabels[field] || field}
          value={toDateSafe(state[field])}
          onChange={(newValue) =>
            setState((prev) => ({
              ...prev,
              [field]: newValue || null, // store Date object
            }))
          }
          slotProps={{
            textField: {
             // required: true,
              size: "small",
              name: field,
              placeholder: "MM/DD/YYYY",
              InputLabelProps: { shrink: true },
              InputProps: {
                sx: {
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  fontSize: "12px",
                  height: 35,
                  "& .MuiInputLabel-root": {
                    fontSize: "12px",
                    color: "#000000",
                  },
                  backgroundColor: "#fafafa",
                },
              },
              sx: commonSx,
            },
          }}
        />
      </LocalizationProvider>
    ) : (
      <TextField
        label={
          field === "proj_name"
            ? `${fieldLabels[field] || field} *` // Add asterisk only for project name
            : fieldLabels[field] || field
        }
        value={state[field] || ""}
        name={field}
        onChange={(e) => {
          setState((prev) => ({ ...prev, [field]: e.target.value }));
          if (field === "proj_name") {
            setErrors((prev) => ({ ...prev, proj_name: "" })); // clear error
          }
        }}
        InputLabelProps={{ shrink: true }}
         sx={{
    ...commonSx,

    // move label slightly upward
    "& .MuiInputLabel-root": {
      transform: "translate(14px, -11px) scale(0.75)", // adjust -11px for label position
    },
  }}
        {...sharedProps}
        error={field === "proj_name" && Boolean(errors.proj_name)} // only for proj_name
        helperText={field === "proj_name" ? errors.proj_name : ""} // show error msg
      />
    );
  };

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await fetch(`${BASE_URL}/clients/${boId}`);
        const data = await res.json();
        setClients(data);
      } catch (err) {
        console.error("Error fetching clients:", err);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/clientprojects/get-managers-tls/${boId}`
        );
        const data = await res.json();
        const map = {};
        data.forEach((emp) => {
          map[emp.employee_id] = emp.employee_name;
        });
        setEmployeeNameMap(map);
        
        const normalizeRoles = (roles) => {
          if (!roles) return [];
          return Array.isArray(roles)
            ? roles.map((r) => r.toLowerCase())
            : [roles.toLowerCase()];
        };
        
        setManagers(
          data.filter((emp) => {
            const roles = normalizeRoles(emp.roles);
            return ['project manager', 'pm', 'pms', 'project managers'].some((role) =>
              roles.includes(role)
            );
          })
        );
        setTLs(
          data.filter((emp) => {
            const roles = normalizeRoles(emp.roles);
            return ['technical lead', 'tl', 'tls', 'technical leads'].some((role) =>
              roles.includes(role)
            );
          })
        );
        setHRs(
          data.filter((emp) => {
            const roles = normalizeRoles(emp.roles);
            return ['human resource', 'hr', 'hrs', 'human resources'].some((role) =>
              roles.includes(role)
            );
          })
        );
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    };

    fetchEmployees();
  }, []);
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/commonVariables/countries`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const countries = await response.json();

        if (!Array.isArray(countries)) {
          throw new Error("Invalid dropdown data format received");
        }

        setCountries(countries); //  assuming you have state like: const [countries, setCountries] = useState([]);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setSnackbarMsg(error.message || "Failed to load dropdown data.");
        setShowSnackbar(true);
      }
    };

    fetchCountries();
  }, []);

  // --- fetch once on page load ---
  const fetchDropdown = async (type, setter) => {
    try {
      const response = await fetch(
        `${BASE_URL}/commonVariables/${type}`
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setter(data); // directly set the array
      } else {
        throw new Error("Invalid dropdown data format received");
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      setSnackbarMsg(error.message || `Failed to load ${type}.`);
      setShowSnackbar(true);
    }
  };

  useEffect(() => {
    fetchDropdown("RISK_LEVEL", setRiskLevel);
    fetchDropdown("PROJECT_STATUS", setProjectStatus);
    fetchDropdown("BILLING_TYPE", setBillingTypes);
    fetchDropdown("CURRENCY_CODE", setCurrencyCodes);
    fetchDropdown("TIMEZONE", setTimezones);
    fetchDropdown("approval_level_required", setApprovalLevel);
    fetchDropdown("INVOICING_CYCLE", setInvoiceCycle);
    fetchDropdown("DELIVERY_MODEL", setDeliveryModel);
  }, []);

  const toggleFlag = async (project) => {
    try {
      await axios.put(
        `${BASE_URL}/clientprojects/toggle-flag/${project.cl_proj_id}/${boId}`,
        { currentFlag: project.delete_flag } // send current flag
      );
      setAlertMsg("Toggle flag updated successfully!");
      setAlertSeverity("success");
      fetchClientProjects();
    } catch (err) {
      console.error("Toggle flag error:", err);
      setAlertMsg(`Toggle flag update failed: ${err.message}`);
      setAlertSeverity("error");
    }
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0"); // month is 0-based
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  useEffect(() => {
    if (alertMsg) {
      const timer = setTimeout(() => {
        setAlertMsg("");
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alertMsg]);

  const handleAdd = async () => {
    try {
      const { start_date, planned_end_date, actual_enddate, approval_level_required } = newProject;

      // Conditional required fields based on approval level
      const requiredFields = {
        proj_name: "Project Name is required",
        approval_level_required: "Approval Level is required",
      };
      
      const approvalLevel = Number(approval_level_required);
      
      // Level 1: TL required
      // Level 2: TL + PM required
      // Level 3: TL + PM + HR required
      if (approvalLevel >= 1) {
        requiredFields.technicalleadid = "Technical Lead is required";
      }
      if (approvalLevel >= 2) {
        requiredFields.projectmanagerid = "Project Manager is required";
      }
      if (approvalLevel === 3) {
        requiredFields.hrid = "HR Manager is required";
      }

      let newErrors = {};
      for (const [field, message] of Object.entries(requiredFields)) {
        if (!newProject[field] || newProject[field].toString().trim() === "") {
          newErrors[field] = message;
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors); // show only missing ones
        return;
      } else {
        setErrors({}); // clear all errors
      }
      const start = start_date ? new Date(start_date) : null;
      const plannedEnd = planned_end_date ? new Date(planned_end_date) : null;
      const actualEnd = actual_enddate ? new Date(actual_enddate) : null;

      if (start && plannedEnd && plannedEnd < start) {
        setSnackbarMsg("Planned End date must be after Start date");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
        return;
      }
      if (start && actualEnd && actualEnd < start) {
        setSnackbarMsg("Actual End date must be after Start date");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
        return;
      }
      await axios.post(`${BASE_URL}/clientprojects/add`, {
        ...newProject,
        start_date: formatDateLocal_TF(start_date),
        planned_end_date: formatDateLocal_TF(planned_end_date),
        actual_enddate: formatDateLocal_TF(actual_enddate),
        cl_id,
        bo_id: boId,
        delete_flag: "N",
        created_by: empId,
        modified_by: empId,
        data_source: "UI",
      });
      setAlertMsg("Record added successfully! ");
      setAlertSeverity("success");
      fetchClientProjects();
      setActiveForm(null);
      setNewProject({ cl_id, bo_id });
    } catch (err) {
      console.error("Add error:", err);
      // If the backend sends a response with status 400 and a JSON message
      if (
        err.response &&
        err.response.status === 400 &&
        err.response.data?.error
      ) {
        setSnackbarMsg(err.response.data.error);
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      } else {
        // fallback for unexpected errors
        setSnackbarMsg(err.message || "Something went wrong");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    }
  };
  const handleUpdate = async () => {
    try {
      const { start_date, planned_end_date, actual_enddate } = editProject;

      const start = start_date ? new Date(start_date) : null;
      const plannedEnd = planned_end_date ? new Date(planned_end_date) : null;
      const actualEnd = actual_enddate ? new Date(actual_enddate) : null;
      if (start && plannedEnd && plannedEnd < start) {
        setSnackbarMsg("Planned End date must be after Start date");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
        return;
      }
      if (start && actualEnd && actualEnd < start) {
        setSnackbarMsg("Actual End date must be after Start date");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
        return;
      }
      // Send editProject state after formatting dates
      const payload = {
        ...editProject,
        start_date: formatDateLocal_TF(editProject.start_date),
        planned_end_date: formatDateLocal_TF(editProject.planned_end_date),
        actual_enddate: formatDateLocal_TF(editProject.actual_enddate),
        cl_id,
        bo_id: boId,
        modified_by: empId,
      };
      await axios.put(
        `${BASE_URL}/clientprojects/update/${editProject.cl_proj_id}`,
        payload
      );
      setAlertMsg("Record updated successfully!");
      setAlertSeverity("success");
      fetchClientProjects();
      setActiveForm(null);
    } catch (err) {
      console.error("Update error:", err);
      setAlertMsg(`Update failed: ${err.message}`);
      setAlertSeverity("error");
    }
  };

  const handleDelete = (cl_proj_id) => {
    setDeleteId(cl_proj_id);
    setConfirmOpen(true);
  };

  const confirmDelete = async (cl_proj_id) => {
    try {
      await axios.delete(
        `${BASE_URL}/clientprojects/delete/${deleteId}/${boId}`
      );
      setAlertMsg("Project deleted");
      setAlertSeverity("success");
      fetchClientProjects();
    } catch (err) {
      console.error("Delete error:", err);
      setAlertMsg(`Failed to delete record: ${err.message}`);
      setAlertSeverity("error");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };
  return (
    <ThemeProvider theme={clientProjectTheme}>
      <CssBaseline />
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to permanently delete this project?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        //onClose={onClose}
        onClose={() => {
          onClose();
          setFormState(initialFormState);
          setFieldErrors({});
        }}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 5,
            overflow: "hidden",
            width: "1100px",     // increase width
            height: "55vh",
            maxWidth: "95vw",
            margin: "0 auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderRadius: "12px",
            background: "linear-gradient(45deg, #e6f0fa, #ffffff)",
            p: 2,
            fontSize: "14px",
            fontWeight: 550,
            fontFamily: "Public Sans, sans-serif",
          }}
        >
          Projects for Client: {clientName}
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: "10px 24px",
            display: "flex",
            flexDirection: "column",
            // alignItems: "center", // Center horizontally
            alignItems: "stretch",
            backgroundColor: "#fff",
            mt: 0,
          }}
        >
          {" "}
          {!activeForm && (
            <Box mb={2} display="flex" justifyContent="flex-end">
              {hasPermission("Client Projects", "Edit") && (
                <Button
                  variant="contained"
                  sx={{
                    // fontWeight: "bold",
                    fontFamily: "Public Sans, sans-serif",
                    borderRadius: "8px",
                    px: 1,
                    height: 36,
                    backgroundColor: "#2DB0E4",
                  }}
                  onClick={() => {
                    setNewProject({ cl_id, bo_id });
                    setActiveForm("add");
                    setFormState(initialFormState);
                    setErrors({});
                    setFieldErrors({});
                  }}
                >
                  Add Project
                </Button>
              )}
            </Box>
          )}
          <Grid
            container
            rowSpacing={2}
            columnSpacing={1}
            sx={{ width: "100%", p: 1 }}
          >
            {["add", "edit"].includes(activeForm) && (
              <Grid container spacing={1}>
                {formFields
                  .map((field) => (
                    <Grid item xs={12} sm={6} md={2.4} key={field}>
                      {renderField(
                        field,
                        activeForm === "add" ? newProject : editProject,
                        activeForm === "add" ? setNewProject : setEditProject,
                        fieldErrors
                      )}
                    </Grid>
                  ))}
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={activeForm === "add" ? handleAdd : handleUpdate}
                    sx={{
                      px: 2.5,
                      height: 34,
                      backgroundColor: "#2db0e4",
                      color: "#fff",
                      textTransform: "none",
                      fontSize: "0.85rem",
                      mr: 1,
                    }}
                  >
                    {activeForm === "add" ? "Save" : "Update"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setActiveForm(null);
                      setFormState(initialFormState);
                      setErrors({}); // close dialog
                      setFormState(initialFormState); // reset form values
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "0.85rem",
                    }}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
          {alertMsg && (
            <Box mb={2}>
              <Alert
                onClose={() => setAlertMsg("")}
                variant="standard"
                sx={{
                  color: alertSeverity === "success" ? "#2E9F50" : "#FF0C0C",
                  fontWeight: 500,
                  backgroundColor: "#fff",
                }}
              >
                {alertMsg}
              </Alert>
            </Box>
          )}
          <ThemeProvider theme={Table_theme}>
            <CssBaseline />
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                      <TableCell sx={{}}>Actions</TableCell>
                                        
                    {projectFields.map((f) => (
                      <TableCell
                        key={f}
                        sx={{ fontSize: "0.85rem", fontWeight: 600 }}
                      >
                        {fieldLabels[f]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientProjects.length > 0 ? (
                    clientProjects.map((proj) => (
                      <TableRow key={proj.cl_proj_id}>
                        <TableCell>
                          <Tooltip
                            title={
                              hasPermission("Client Projects", "Edit")
                                ? "Edit"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                sx={{ color: "#2DB0E4" }}
                                size="small"
                                onClick={() => {
                                  if (
                                    hasPermission("Client Projects", "Edit")
                                  ) {
                                    setEditProject(proj); // store the selected record
                                    setActiveForm("edit"); // open the edit dialog
                                  }
                                }}
                                disabled={
                                  !hasPermission("Client Projects", "Edit")
                                }
                              >
                                <EditOutlinedIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Client Projects", "Delete")
                                ? "Delete"
                                : "No permission"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Client Projects", "Delete") &&
                                  handleDelete(proj.cl_proj_id)
                                }
                                disabled={
                                  !hasPermission("Client Projects", "Delete")
                                }
                                // onClick={() => handleDelete(proj.cl_proj_id)}
                              >
                                <DeleteIcon sx={{ fontSize: "16px" }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip
                            title={
                              hasPermission("Client Projects", "Edit")
                                ? "Toggle Delete Flag"
                                : "No permission"
                            }
                          >
                            {" "}
                            <span>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  hasPermission("Client Projects", "Edit") &&
                                  toggleFlag(proj)
                                }
                                disabled={
                                  !hasPermission("Client Projects", "Edit")
                                }
                                // onClick={() => toggleFlag(proj)}
                              >
                                <FlagIcon
                                  fontSize="small"
                                  style={{
                                    color:
                                      proj.delete_flag === "Y"
                                        ? "#FF0C0C"
                                        : "#2E9F50",
                                    transition: "color 0.2s",
                                    width: 17,
                                    height: 17,
                                    verticalAlign: "middle",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        {projectFields.map((f) => (
                          <TableCell key={f} sx={{ fontSize: "0.85rem" }}>
                            {[
                              "start_date",
                              "planned_end_date",
                              "actual_enddate",
                            ].includes(f)
                              ? proj[f] // only call if value exists
                                ? formatDateLocal(proj[f])
                                : "-" // otherwise show dash
                              : f === "projectmanagerid"
                              ? employeeNameMap[proj.projectmanagerid] ||
                                proj.projectmanagerid ||
                                "-"
                              : f === "technicalleadid"
                              ? employeeNameMap[proj.technicalleadid] ||
                                proj.technicalleadid ||
                                "-"
                              : f === "hrid"
                              ? employeeNameMap[proj.hrid] || "-"
                              : proj[f] || "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={projectFields.length + 1}
                        align="center"
                      >
                        No project records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ThemeProvider>
        </DialogContent>
        <DialogActions sx={{ p: "8px 24px", justifyContent: "flex-end" }}>
          <Button
            onClick={() => {
              onClose();
              setActiveForm(null);
              setErrors({}); // close dialog
              setFormState(initialFormState); // reset form values
            }}
            variant="outlined"
            color="inherit"
            sx={{
              fontSize: "0.85rem",
              color: "#555",
              borderColor: "#ccc",
              textTransform: "none",
            }}
          >
            Close
          </Button>
        </DialogActions>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMsg}
          </Alert>
        </Snackbar>
      </Dialog>
    </ThemeProvider>
  );
};
export default ClientProjectSubpage;
